import { type GetAuditLogsParams } from '../api';
import { type AuditLog, type AuditLogChange } from '../types';
import { formatAuditLogChangeValue } from './formatAuditLogChangeValue';

export type AuditTrailChange = {
  field: string;
  before?: string;
  after?: string;
  status: 'added' | 'updated' | 'removed';
};

export type AuditTrailEntry = {
  id: AuditLog['id'];
  actorUserId?: AuditLog['userId'];
  resourceName?: AuditLog['resourceName'];
  resourceId?: AuditLog['resourceId'];
  createdAt: string | number | Date;
  actorName?: string | null;
  actorEmail?: string | null;
  action: 'create' | 'update' | 'delete';
  summary?: string | null;
  message?: string | null;
  changes?: AuditTrailChange[];
};

const getSearchResourceId = (query: string) => {
  const trimmed = query.trim();

  if (!trimmed) {
    return null;
  }

  if (/^\d+$/.test(trimmed)) {
    return trimmed;
  }

  const numericTokens = trimmed.match(/\d+/g) ?? [];

  if (
    numericTokens.length === 1 &&
    (/#/.test(trimmed) || /\b(booking|id|resource|session)\b/i.test(trimmed))
  ) {
    return numericTokens[0];
  }

  return null;
};

const isSessionResourceSearch = (query: string) =>
  /\bsessions?\b/i.test(query.trim());

const getSearchResourceName = (query: string) => {
  const trimmed = query.trim();

  if (isSessionResourceSearch(trimmed)) {
    return 'booking_session';
  }

  if (/\bbookings?\b/i.test(trimmed)) {
    return 'booking';
  }

  return null;
};

export const getAuditLogLookupParams = (
  query: string
): Pick<
  GetAuditLogsParams,
  'search' | 'resource_id' | 'resourceId' | 'resource_name' | 'resourceName'
> => {
  const trimmed = query.trim();

  if (!trimmed) {
    return {};
  }

  const resourceId = getSearchResourceId(trimmed);

  if (resourceId) {
    const resourceName = getSearchResourceName(trimmed) ?? 'booking_session';

    return {
      search: resourceId,
      resource_id: resourceId,
      resourceId,
      resource_name: resourceName,
      resourceName,
    };
  }

  return { search: trimmed };
};

const normalizeSearchValue = (value: unknown) => {
  if (value === null || value === undefined) {
    return null;
  }

  return String(value).trim().toLowerCase();
};

export const getAuditTrailEntrySearchValues = (
  entry: AuditTrailEntry
): string[] =>
  [
    entry.id,
    entry.actorUserId,
    entry.actorName,
    entry.actorEmail,
    entry.summary,
    entry.action,
    entry.resourceName,
    entry.resourceId,
    entry.message,
  ].flatMap((value) => {
    const normalized = normalizeSearchValue(value);

    return normalized ? [normalized] : [];
  });

export const auditTrailEntryMatchesSearch = (
  entry: AuditTrailEntry,
  query: string,
  field: string
) => {
  const text = query.trim().toLowerCase();
  const matchesField =
    !field || entry.changes?.some((change) => change.field === field);

  if (!matchesField) {
    return false;
  }

  if (!text) {
    return true;
  }

  const headerMatch = getAuditTrailEntrySearchValues(entry).some((value) =>
    value.includes(text)
  );

  if (headerMatch) {
    return true;
  }

  const changesMatch = entry.changes?.some((change) => {
    if (field && change.field !== field) {
      return false;
    }

    const haystack = `${change.field} ${change.before ?? ''} ${
      change.after ?? ''
    }`.toLowerCase();

    return haystack.includes(text);
  });

  return Boolean(changesMatch);
};

export const filterAuditTrailEntries = (
  entries: AuditTrailEntry[],
  query: string,
  field: string
) =>
  entries.filter((entry) => auditTrailEntryMatchesSearch(entry, query, field));

export const areAuditTrailEntriesConsistentWithSearch = ({
  entries,
  query,
  field,
}: {
  entries: AuditTrailEntry[];
  query: string;
  field: string;
}) =>
  !query.trim() && !field
    ? true
    : entries.every((entry) =>
        auditTrailEntryMatchesSearch(entry, query, field)
      );

type MapChangesOptions = {
  action: string | null | undefined;
};

const determineChangeStatus = (
  change: AuditLogChange,
  options: MapChangesOptions
): AuditTrailChange['status'] => {
  const { action } = options;
  const hasFrom = change.from !== undefined && change.from !== null;
  const hasTo = change.to !== undefined && change.to !== null;

  if (!hasFrom && hasTo) {
    return 'added';
  }

  if (hasFrom && !hasTo) {
    return 'removed';
  }

  if (!hasFrom && !hasTo) {
    return action?.toLowerCase() === 'delete' ? 'removed' : 'added';
  }

  return 'updated';
};

const formatChangeValue = (value: unknown) =>
  formatAuditLogChangeValue(value).displayValue;

export const mapAuditLogsToTrailEntries = (
  logs: AuditLog[]
): AuditTrailEntry[] => {
  if (!Array.isArray(logs)) {
    return [];
  }

  return logs.map((log) => {
    const {
      action,
      userId,
      userName,
      userEmail,
      resourceName,
      resourceId,
      message,
      changes,
      createdAt,
    } = log;

    const normalizedAction: AuditTrailEntry['action'] = (() => {
      switch (action?.toLowerCase()) {
        case 'create':
          return 'create';
        case 'delete':
        case 'remove':
          return 'delete';
        default:
          return 'update';
      }
    })();

    const summary = (() => {
      if (resourceName && resourceId) {
        return `${resourceName} • ${resourceId}`;
      }

      if (resourceName) {
        return resourceName;
      }

      if (message) {
        return message;
      }

      return action ?? null;
    })();

    const mappedChanges: AuditTrailChange[] | undefined = changes?.map(
      (change) => ({
        field: change.field,
        before:
          change.from !== undefined
            ? formatChangeValue(change.from)
            : undefined,
        after:
          change.to !== undefined ? formatChangeValue(change.to) : undefined,
        status: determineChangeStatus(change, { action }),
      })
    );

    return {
      id: log.id,
      actorUserId: userId,
      resourceName,
      resourceId,
      createdAt,
      actorName: userName,
      actorEmail: userEmail,
      action: normalizedAction,
      summary,
      message,
      changes: mappedChanges,
    };
  });
};
